import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { api, type BankAccountSummary, type WithdrawalAlert, type AccountBreakdown } from '../api/client'
import { UndoToast, ConfirmDialog } from '../components/ConfirmDialog'
import { fetchMonthScheduleEvents, groupEventsByDate } from '../utils/scheduleEvents'
import TodayProductionPlanPanel from '../components/TodayProductionPlanPanel'
import {
  DndContext, DragOverlay, closestCorners,
  PointerSensor, KeyboardSensor, useSensor, useSensors,
  type DragStartEvent, type DragOverEvent, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// ────────────────────────────────────────
// 型
// ────────────────────────────────────────
type DashData = {
  today: string
  upcoming_deadlines: any[]
  unpaid_expenses: any[]
  today_attendance: any[]
  pending_revenues: any[]
  recent_reports: any[]
  monthly_labor: {
    total: number; working_days: number; man_days: number; meal_total: number
    help_total?: number; combined_total?: number
  } | null
  monthly_revenue: { received: number; tax_reserved: number; pending_income: number } | null
  spendable: number
  labor_chart: { month: string; total: number; man_days: number }[]
  shift_needs_review_count: number
  bank_accounts?: BankAccountSummary[]
  withdrawal_alerts?: WithdrawalAlert[]
  account_breakdown?: AccountBreakdown
}

// ────────────────────────────────────────
// 完了済み話数の判定
// ────────────────────────────────────────
const DONE_STATUSES = new Set(['completed', 'submitted', 'hiatus'])

function isDoneEpisode(ep: any): boolean {
  const status = String(ep?.status ?? ep?.episode_status ?? '').toLowerCase()
  if (DONE_STATUSES.has(status)) return true
  const stagesDone  = Number(ep?.stages_done  ?? ep?.done_stages  ?? NaN)
  const stagesTotal = Number(ep?.stages_total ?? ep?.total_stages ?? 7)
  if (Number.isFinite(stagesDone) && stagesDone >= stagesTotal && stagesTotal > 0) return true
  const summary = String(ep?.stage_summary ?? '')
  if (summary) {
    const pairs = summary.split(',').map((pair: string) => pair.split(':'))
    const known = pairs.filter(([stage, st]: string[]) => stage && st)
    if (known.length > 0 && known.every(([, st]: string[]) => st === 'done')) return true
  }
  return false
}

// ────────────────────────────────────────
// 残日数ユーティリティ
// ────────────────────────────────────────
function urgencyClass(days: number | null): string {
  if (days === null) return ''
  if (days < 0)  return 'deadline-critical'
  if (days <= 2) return 'deadline-critical'
  if (days <= 5) return 'deadline-warning'
  return 'deadline-ok'
}

function DaysBadge({ days, label }: { days: number | null; label?: string }) {
  if (days === null) return <span className="text-muted text-sm">—</span>
  const cls  = urgencyClass(days)
  const text = days < 0 ? `${Math.abs(days)}日超過` : days === 0 ? '今日' : `残${days}日`
  return <span className={`deadline-chip ${cls}`}>{label ? `${label} ` : ''}{text}</span>
}

// ────────────────────────────────────────
// 工程ピップ
// ────────────────────────────────────────
const STAGE_LABELS: Record<string, string> = {
  name: 'ネーム', name_revision: '修正', sketch: '下書き',
  inking: 'ペン', background: '背景', finishing: '仕上げ', submitted: '納品',
}
const STAGE_KEYS = Object.keys(STAGE_LABELS)

function StagePips({ summary }: { summary?: string }) {
  const map: Record<string, string> = {}
  for (const pair of (summary ?? '').split(',')) {
    const [s, st] = pair.split(':')
    if (s && st) map[s] = st
  }
  const done  = STAGE_KEYS.filter((k) => map[k] === 'done').length
  const total = STAGE_KEYS.length
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div className="stage-bar">
        {STAGE_KEYS.map((k) => (
          <div key={k} className={`stage-pip ${map[k] ?? 'not_started'}`}
            title={`${STAGE_LABELS[k]}: ${map[k] ?? '未着'}`} />
        ))}
      </div>
      <span style={{ fontSize: 10, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
        {done}/{total}
      </span>
    </div>
  )
}

// ────────────────────────────────────────
// グラフコンポーネント群（変更なし）
// ────────────────────────────────────────
const LABOR_TARGET = 450000

function CashflowBar({ received, taxReserved, labor, fixedExpense, spendable }: {
  received: number; taxReserved: number; labor: number; fixedExpense: number; spendable: number
}) {
  if (received <= 0) return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>入金データなし</div>
  const pct = (v: number) => Math.max(0, Math.min(100, Math.round(v / received * 100)))
  const segments = [
    { label: '税積立',   value: taxReserved,            color: 'var(--chart-tax)', p: pct(taxReserved) },
    { label: '人件費',   value: labor,                  color: 'var(--chart-labor)', p: pct(labor) },
    { label: '固定費等', value: fixedExpense,            color: 'var(--chart-fixed)', p: pct(fixedExpense) },
    { label: '残',       value: Math.max(0, spendable), color: '#2d7a3a', p: pct(Math.max(0, spendable)) },
  ].filter((s) => s.p > 0)
  return (
    <div>
      <div style={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
        {segments.map((s) => (
          <div key={s.label} style={{ width: `${s.p}%`, background: s.color }}
            title={`${s.label}: ¥${s.value.toLocaleString()} (${s.p}%)`} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 7, flexWrap: 'wrap' }}>
        {segments.map((s) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--ink-3)' }}>{s.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{s.p}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function DonutChart({ segments }: { segments: { label: string; value: number; color: string }[] }) {
  const total = segments.reduce((s, g) => s + g.value, 0)
  if (total <= 0) return <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>データなし</div>
  const R = 36; const cx = 44; const cy = 44
  let angle = -Math.PI / 2
  const arcs = segments.map((seg) => {
    const a = (seg.value / total) * 2 * Math.PI
    const x1 = cx + R * Math.cos(angle); const y1 = cy + R * Math.sin(angle)
    const x2 = cx + R * Math.cos(angle + a); const y2 = cy + R * Math.sin(angle + a)
    const d = `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${a > Math.PI ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`
    angle += a
    return { ...seg, d, p: Math.round(seg.value / total * 100) }
  })
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg width="88" height="88" style={{ flexShrink: 0 }}>
        {arcs.map((a) => <path key={a.label} d={a.d} fill={a.color} opacity="0.85" />)}
        <circle cx={cx} cy={cy} r={R * 0.52} fill="var(--paper-2)" />
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {arcs.filter((a) => a.p > 0).map((a) => (
          <div key={a.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: a.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--ink-3)' }}>{a.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{a.p}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function LaborChart({ data }: { data: { month: string; total: number }[] }) {
  if (!data.length) return <div className="loading" style={{ padding: 24 }}>データなし</div>
  const W = 420; const H = 96; const PAD = { t: 8, r: 8, b: 28, l: 54 }
  const innerW = W - PAD.l - PAD.r; const innerH = H - PAD.t - PAD.b
  const maxVal = Math.max(...data.map((d) => d.total), LABOR_TARGET) * 1.1
  const barW   = Math.min(36, innerW / data.length - 6)
  const yPct   = (v: number) => innerH - (v / maxVal) * innerH
  const targetY = yPct(LABOR_TARGET) + PAD.t
  const fmt = (n: number) => n >= 10000 ? `${Math.round(n / 10000)}万` : String(n)
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
      <line x1={PAD.l} y1={targetY} x2={W - PAD.r} y2={targetY}
        stroke="var(--red)" strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
      <text x={PAD.l - 4} y={targetY + 4} textAnchor="end"
        fontSize="9" fill="var(--red)" opacity="0.7">目標</text>
      {data.map((d, i) => {
        const x = PAD.l + (i / data.length) * innerW + (innerW / data.length - barW) / 2
        const barH = (d.total / maxVal) * innerH
        const y = PAD.t + innerH - barH
        const over = d.total > LABOR_TARGET
        return (
          <g key={d.month}>
            <rect x={x} y={y} width={barW} height={barH}
              fill={over ? 'var(--red)' : 'var(--ink-3)'} opacity="0.7" rx="2" />
            <text x={x + barW / 2} y={H - PAD.b + 14} textAnchor="middle"
              fontSize="9" fill="var(--ink-3)">{d.month.slice(5)}</text>
            <text x={x + barW / 2} y={y - 2} textAnchor="middle"
              fontSize="8" fill={over ? 'var(--red)' : 'var(--ink-3)'}>{fmt(d.total)}</text>
          </g>
        )
      })}
      {[-1, 0, 1, 2, 3].map((i) => {
        const val = (maxVal / 4) * (4 - i - 1)
        const yy  = PAD.t + (i / 3) * innerH
        return (
          <text key={i} x={PAD.l - 6} y={yy + 4} textAnchor="end"
            fontSize="9" fill="var(--ink-3)">{fmt(val)}</text>
        )
      })}
    </svg>
  )
}

// ────────────────────────────────────────
// ★ 新規: 残高調整モーダル
// ────────────────────────────────────────
function AdjustModal({ accountKey, accountName, currentBalance, onClose, onAdjusted }: {
  accountKey: string
  accountName: string
  currentBalance: number
  onClose: () => void
  onAdjusted: () => void
}) {
  const [amount,  setAmount]  = useState('')
  const [reason,  setReason]  = useState('')
  const [saving,  setSaving]  = useState(false)
  const [err,     setErr]     = useState<string | null>(null)

  const preview = Number(amount)
  const newBal  = Number.isFinite(preview) ? currentBalance + preview : currentBalance

  const handleSubmit = async () => {
    const adj = Number(amount)
    if (!Number.isFinite(adj) || adj === 0) { setErr('増減額を入力してください（0 以外の整数）'); return }
    setSaving(true); setErr(null)
    try {
      await api.bankAccounts.adjust(accountKey, { adjustment_amount: Math.round(adj), reason: reason.trim() || undefined })
      onAdjusted(); onClose()
    } catch (e: any) {
      setErr(e.message ?? '調整に失敗しました')
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div className="modal-title">残高調整 — {accountName}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>現在残高</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 700 }}>
              ¥{currentBalance.toLocaleString('ja-JP')}
            </div>
          </div>
          <div className="form-group">
            <label>増減額（円）— 増加はプラス、減少はマイナスで入力</label>
            <input
              type="number"
              placeholder="+100000 または -50000"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setErr(null) }}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </div>
          {amount !== '' && Number.isFinite(preview) && preview !== 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, marginBottom: 12,
              background: newBal >= 0 ? 'var(--green-light)' : 'var(--red-light)',
              border: `1px solid ${newBal >= 0 ? 'var(--green-border)' : 'var(--red-border)'}`,
            }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>調整後残高</div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700,
                color: newBal >= 0 ? 'var(--green)' : 'var(--red)',
              }}>
                ¥{newBal.toLocaleString('ja-JP')}
              </div>
            </div>
          )}
          <div className="form-group">
            <label>理由（任意）</label>
            <input type="text" placeholder="例: 実口座残高に合わせて調整"
              value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {err && (
            <div style={{ color: 'var(--red)', fontSize: 12, padding: '8px 12px',
              background: 'var(--red-light)', borderRadius: 6 }}>
              {err}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '調整中...' : '残高を調整する'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────
// ★ 新規: 内訳の明細行（固定費・支払・給与の1件分をコンパクトに表示）
// ────────────────────────────────────────
const EXPENSE_STATUS_LABEL: Record<string, string> = { unpaid: '未払い', overdue: '期限超過' }
const PAYROLL_STATUS_LABEL: Record<string, string> = {
  draft: '未払い', approved: '承認済み', paid: '支払済み',
  pending: '未提出', submitted: '提出済み', confirmed: '確認済み', cancelled: 'キャンセル',
}

function DetailFmt(n: number) { return n.toLocaleString('ja-JP') }

function DetailRow({ primary, secondary, amount, statusLabel }: {
  primary: string; secondary?: string; amount: number; statusLabel?: string
}) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
      fontSize: 11, padding: '3px 0', borderBottom: '1px dashed var(--paper-3)',
    }}>
      <span style={{ color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
        {primary}
        {secondary && <span style={{ color: 'var(--ink-3)', marginLeft: 6 }}>{secondary}</span>}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {statusLabel && (
          <span style={{
            fontSize: 9, color: 'var(--ink-3)', background: 'rgba(0,0,0,0.05)',
            borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap',
          }}>{statusLabel}</span>
        )}
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--amber)', whiteSpace: 'nowrap' }}>
          ¥{DetailFmt(amount)}
        </span>
      </span>
    </div>
  )
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600, marginBottom: 3 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
    </div>
  )
}

// ────────────────────────────────────────
// ★ 新規: 口座残高カード
// ────────────────────────────────────────
function BankAccountCards({ accounts, accountBreakdown, onAdjust }: {
  accounts: BankAccountSummary[]
  accountBreakdown?: AccountBreakdown
  onAdjust: (accountKey: string) => void
}) {
  // 内訳展開状態を口座キーごとに管理（初期値は全て閉じ）
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const toggleBreakdown = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  // 内訳の中の「詳細（明細一覧）」展開状態を口座キーごとに管理（初期値は全て閉じ）
  const [expandedDetailKeys, setExpandedDetailKeys] = useState<Set<string>>(new Set())
  const toggleDetail = (key: string) =>
    setExpandedDetailKeys((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  if (!accounts.length) return null
  const fmt = (n: number) => n.toLocaleString('ja-JP')

  const ACCOUNT_LABEL: Record<string, string> = {
    business: '💼 事業用口座',
    personal: '👤 漫画家用個人口座',
  }

  return (
    <div className="bank-account-cards">
      {accounts.map((acct) => {
        const proj          = acct.projected_balance
        const isShort       = proj < 0
        const bd            = acct.deduction_breakdown
        const hasBreakdown  = acct.upcoming_withdrawals_total > 0 && !!bd
        const showBreakdown = expandedKeys.has(acct.account_key)

        return (
          <div
            key={acct.account_key}
            className="bank-account-card"
            style={{
              borderColor: isShort ? 'var(--red-border-strong)' : 'var(--paper-3)',
              background:  isShort ? 'var(--red-light)' : undefined,
            }}
          >
            {/* ── ヘッダー行 ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>
                  {ACCOUNT_LABEL[acct.account_key] ?? acct.name}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700,
                  color: isShort ? 'var(--red)' : 'var(--ink)' }}>
                  ¥{fmt(acct.current_balance)}
                </div>
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => onAdjust(acct.account_key)}
                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
              >
                残高調整
              </button>
            </div>

            {/* ── 引き落とし合計 + 差引後残高 ── */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>引き落とし予定合計</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--amber)' }}>
                  ▼ ¥{fmt(acct.upcoming_withdrawals_total)}
                  {hasBreakdown && (
                    <button
                      onClick={() => toggleBreakdown(acct.account_key)}
                      style={{
                        marginLeft: 6, fontSize: 10, color: 'var(--ink-3)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        textDecoration: 'underline', padding: 0, fontFamily: 'inherit',
                      }}
                    >
                      {showBreakdown ? '閉じる' : '内訳'}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>引き落とし後の見込み残高</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700,
                  color: proj >= 0 ? 'var(--green)' : 'var(--red)' }}>
                  {proj >= 0 ? '+' : ''}¥{fmt(proj)}
                  {isShort && <span style={{ fontSize: 10, marginLeft: 4 }}>⚠ 不足</span>}
                </div>
              </div>
            </div>

            {/* ── 内訳（展開時のみ表示） ── */}
            {showBreakdown && bd && (
              <div style={{
                marginTop: 10,
                background: 'rgba(0,0,0,0.03)', borderRadius: 6,
                padding: '8px 12px',
              }}>
                <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 6, fontWeight: 600 }}>
                  差し引き予定内訳
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {bd.expenses_total > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--ink-2)' }}>固定費・支払い</span>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--amber)' }}>
                        ▼ ¥{fmt(bd.expenses_total)}
                      </span>
                    </div>
                  )}
                  {(bd.confirmed_salary_total ?? 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--ink-2)' }}>確定済み給与（通常アシスタント）</span>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--amber)' }}>
                        ▼ ¥{fmt(bd.confirmed_salary_total!)}
                      </span>
                    </div>
                  )}
                  {(bd.confirmed_help_salary_total ?? 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--ink-2)' }}>確定済み給与（ヘルプスタッフ）</span>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--amber)' }}>
                        ▼ ¥{fmt(bd.confirmed_help_salary_total!)}
                      </span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', fontSize: 12,
                    borderTop: '1px solid var(--paper-3)', paddingTop: 4, marginTop: 2,
                  }}>
                    <span style={{ color: 'var(--ink-3)' }}>合計</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--amber)' }}>
                      ▼ ¥{fmt(bd.total)}
                    </span>
                  </div>
                </div>

                {/* ── さらに詳細（明細一覧）。内訳の中にネストした折りたたみ ── */}
                {(() => {
                  const acctKey = acct.account_key
                  const fixedCostsForAcct = (accountBreakdown?.fixed_costs ?? []).filter((x) => x.account_key === acctKey)
                  const paymentsForAcct   = (accountBreakdown?.payments ?? []).filter((x) => x.account_key === acctKey)
                  const assistantPayroll  = acctKey === 'business' ? (accountBreakdown?.assistant_payroll ?? []) : []
                  const helpPayroll       = acctKey === 'business' ? (accountBreakdown?.help_payroll ?? []) : []
                  const hasDetail = fixedCostsForAcct.length > 0 || paymentsForAcct.length > 0
                    || assistantPayroll.length > 0 || helpPayroll.length > 0
                  if (!hasDetail) return null
                  const showDetail = expandedDetailKeys.has(acctKey)
                  return (
                    <div style={{ marginTop: 6 }}>
                      <button
                        onClick={() => toggleDetail(acctKey)}
                        style={{
                          fontSize: 10, color: 'var(--ink-3)', background: 'none', border: 'none',
                          cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: 'inherit',
                        }}
                      >
                        {showDetail ? '詳細を閉じる' : '∨ 詳細（明細一覧）'}
                      </button>
                      {showDetail && (
                        <div style={{ marginTop: 4, maxHeight: 260, overflowY: 'auto' }}>
                          {fixedCostsForAcct.length > 0 && (
                            <DetailSection title="固定費">
                              {fixedCostsForAcct.map((item) => (
                                <DetailRow key={item.id}
                                  primary={item.name}
                                  secondary={`${item.due_at ?? '期日未設定'}${item.target_month ? ` / ${item.target_month}` : ''}`}
                                  amount={item.amount}
                                  statusLabel={EXPENSE_STATUS_LABEL[item.status] ?? item.status}
                                />
                              ))}
                            </DetailSection>
                          )}
                          {paymentsForAcct.length > 0 && (
                            <DetailSection title="支払管理">
                              {paymentsForAcct.map((item) => (
                                <DetailRow key={item.id}
                                  primary={item.name}
                                  secondary={`${item.due_at ?? '期日未設定'} / ${item.category}${item.target_month ? ` / ${item.target_month}` : ''}`}
                                  amount={item.amount}
                                  statusLabel={EXPENSE_STATUS_LABEL[item.status] ?? item.status}
                                />
                              ))}
                            </DetailSection>
                          )}
                          {assistantPayroll.length > 0 && (
                            <DetailSection title="通常アシスタント給与">
                              {assistantPayroll.map((row) => (
                                <DetailRow key={`${row.target_month}:${row.status}`}
                                  primary={row.target_month}
                                  secondary={`${row.count}名`}
                                  amount={row.total_amount}
                                  statusLabel={PAYROLL_STATUS_LABEL[row.status] ?? row.status}
                                />
                              ))}
                            </DetailSection>
                          )}
                          {helpPayroll.length > 0 && (
                            <DetailSection title="ヘルプスタッフ給与">
                              {helpPayroll.map((row) => (
                                <DetailRow key={`${row.target_month}:${row.status}`}
                                  primary={row.target_month}
                                  secondary={`${row.count}件`}
                                  amount={row.total_amount}
                                  statusLabel={PAYROLL_STATUS_LABEL[row.status] ?? row.status}
                                />
                              ))}
                            </DetailSection>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ────────────────────────────────────────
// ★ 新規: 残高不足アラートバナー
// ────────────────────────────────────────
function WithdrawalAlertBanner({ alerts }: { alerts: WithdrawalAlert[] }) {
  if (!alerts.length) return null
  const fmt = (n: number) => n.toLocaleString('ja-JP')

  return (
    <div className="withdrawal-alert-banner" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>
        ⚠ 残高不足の可能性あり
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {alerts.map((a) => {
          const shortage = Math.abs(a.projected_balance_after)
          const dateStr  = a.due_at.slice(5).replace('-', '/')
          return (
            <div key={`${a.account_key}-${a.expense_id}`}
              style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--amber)', fontWeight: 600 }}>
                {a.days_until_due === 0 ? '今日' : a.days_until_due === 1 ? '明日' : `${dateStr}`}
              </span>
              <span>{a.description}の引き落としで、</span>
              <span style={{ fontWeight: 600 }}>{a.account_name}</span>
              <span>が</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--red)' }}>
                ¥{fmt(shortage)}
              </span>
              <span>不足見込みです</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ────────────────────────────────────────
// 勤怠パネル（変更なし）
// ────────────────────────────────────────
// [AttendancePanel は元ファイルからそのまま持ち込み]
// 長くなるので変更ない関数は省略表記にします。
// 以下の AttendancePanel は元ファイルと同一実装です。

type AttItem = {
  assistant_id: string; name: string; role_type: string
  check_in: string | null; check_out: string | null
  overtime_min: number | null; transport_fee: number | null
  meal_provided: number; daily_total: number | null; is_recorded: number
}

function AttendancePanel({ list, today, onRecorded }: {
  list: AttItem[]; today: string
  onRecorded: (id: string, name: string) => void
}) {
  const [loading, setLoading]  = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const quickRecord = async (assistantId: string, name: string) => {
    setLoading(assistantId); setErrorMsg(null)
    try {
      const res = await api.attendance.quickRecord(assistantId, today)
      onRecorded((res as any).id ?? '', name)
    } catch (e: any) {
      setErrorMsg(e.message ?? '記録に失敗しました')
    } finally { setLoading(null) }
  }

  return (
    <div>
      {errorMsg && (
        <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8,
          padding: '6px 10px', background: 'var(--red-light)', borderRadius: 4 }}>
          {errorMsg}
        </div>
      )}
      {list.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>アシスタントなし</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((a) => (
            <div key={a.assistant_id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
              borderBottom: '1px solid var(--paper-2)',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: a.is_recorded ? 'var(--green)' : 'var(--paper-3)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</div>
                {a.is_recorded ? (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {a.check_in ? `${a.check_in}〜${a.check_out ?? '?'}` : '記録済み'}
                    {a.daily_total ? ` ¥${a.daily_total.toLocaleString()}` : ''}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{a.role_type}</div>
                )}
              </div>
              {!a.is_recorded && (
                <button
                  className="btn btn-sm btn-outline"
                  style={{ fontSize: 11 }}
                  disabled={loading === a.assistant_id}
                  onClick={() => quickRecord(a.assistant_id, a.name)}
                >
                  {loading === a.assistant_id ? '...' : '出勤記録'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────
// EventCalendar（変更なし — 元ファイルから移植）
// ────────────────────────────────────────
const todayStr = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)

function EventCard({ event, onUpdated }: { event: any; onUpdated: () => void }) {
  const [editing,  setEditing]  = useState(false)
  const [form,     setForm]     = useState({
    title: event.title, event_type: event.event_type ?? 'todo', memo: event.memo ?? ''
  })
  const [isDone,   setIsDone]   = useState(!!event.is_done)
  const [saving,   setSaving]   = useState(false)
  const [deleting, setDeleting] = useState(false)
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    try {
      await api.events.update(event.id, {
        title: form.title, event_type: form.event_type,
        memo: form.memo.trim() || null, is_done: isDone ? 1 : 0,
      })
      setEditing(false); onUpdated()
    } finally { setSaving(false) }
  }

  const del = async () => {
    setDeleting(true)
    try { await api.events.delete(event.id); onUpdated() }
    finally { setDeleting(false) }
  }

  const toggleDone = async () => {
    const next = !isDone; setIsDone(next)
    await api.events.update(event.id, { is_done: next ? 1 : 0 }).catch(() => setIsDone(!next))
    onUpdated()
  }

  const typeColor: Record<string, string> = {
    todo: 'var(--amber)', deadline: 'var(--red)', memo: 'var(--ink-3)', payday: 'var(--green)',
  }
  const color = typeColor[event.event_type ?? 'todo'] ?? 'var(--ink-3)'

  if (editing) {
    return (
      <div style={{ padding: '8px 10px', border: '1px solid var(--paper-3)', borderRadius: 6, background: 'var(--paper-2)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input style={{ flex: 1, fontSize: 12 }} value={form.title}
            onChange={e => set('title', e.target.value)} />
          <select style={{ fontSize: 11 }} value={form.event_type}
            onChange={e => set('event_type', e.target.value)}>
            <option value="todo">TODO</option>
            <option value="deadline">締切</option>
            <option value="memo">メモ</option>
            <option value="payday">給料日</option>
          </select>
        </div>
        <input style={{ width: '100%', fontSize: 11, marginBottom: 6 }}
          placeholder="メモ（任意）" value={form.memo} onChange={e => set('memo', e.target.value)} />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>キャンセル</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>保存</button>
        </div>
      </div>
    )
  }
  return (
    <div style={{
      display: 'flex', gap: 6, padding: '5px 6px', borderRadius: 5,
      background: isDone ? 'transparent' : 'var(--paper)',
      border: `1px solid ${isDone ? 'transparent' : 'var(--paper-3)'}`,
      opacity: isDone ? 0.5 : 1,
    }}>
      <input type="checkbox" checked={isDone} onChange={toggleDone} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, textDecoration: isDone ? 'line-through' : undefined,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={event.title}>
          <span style={{ color, marginRight: 4, fontSize: 10 }}>●</span>
          {event.title}
        </div>
        {event.memo && (
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {event.memo}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '1px 5px' }}
          onClick={() => setEditing(true)}>編集</button>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '1px 5px', color: 'var(--red)' }}
          onClick={del} disabled={deleting}>削除</button>
      </div>
    </div>
  )
}

// ────────────────────────────────────────
// 日付詳細ポップオーバー
// ────────────────────────────────────────
const TYPE_LABEL: Record<string, string> = {
  todo: 'TODO', deadline: '締切', memo: 'メモ', payday: '給料日',
}
const DOW_LABEL = ['日', '月', '火', '水', '木', '金', '土']

function DayPopover({ anchorEl, dateStr, events, typeColor, onClose, onDetailEvent }: {
  anchorEl: HTMLElement | null
  dateStr: string
  events: any[]
  typeColor: Record<string, string>
  onClose: () => void
  onDetailEvent: (e: any) => void
}) {
  const d = new Date(`${dateStr}T00:00:00+09:00`)
  const dow = DOW_LABEL[d.getDay()]
  const label = `${dateStr}（${dow}）`

  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // anchorEl の位置から fixed 座標を計算
  useLayoutEffect(() => {
    if (!anchorEl) return
    const rect = anchorEl.getBoundingClientRect()
    const POP_W = 260
    const POP_H = 300 // 最大想定高さ（実際はcontent次第）
    const MARGIN = 6
    const vw = window.innerWidth
    const vh = window.innerHeight

    let top  = rect.bottom + MARGIN
    let left = rect.left

    // 下にはみ出す場合 → セルの上に表示
    if (top + POP_H > vh - MARGIN) {
      top = rect.top - POP_H - MARGIN
      // それでも上にはみ出す場合は画面上端に固定
      if (top < MARGIN) top = MARGIN
    }

    // 右にはみ出す場合 → 左へ寄せる
    if (left + POP_W > vw - MARGIN) {
      left = vw - POP_W - MARGIN
    }

    // 左にはみ出す場合 → 右へ戻す
    if (left < MARGIN) left = MARGIN

    setPos({ top, left })
  }, [anchorEl])

  // 外側クリックで閉じる
  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(ev.target as Node)) {
        onClose()
      }
    }
    // 次の tick で登録（今回のクリックを拾わないように）
    const t = setTimeout(() => document.addEventListener('click', handler), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('click', handler)
    }
  }, [onClose])

  // Esc で閉じる
  useEffect(() => {
    const handler = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const popover = (
    <div
      ref={popRef}
      className="cal-popover"
      style={pos ? { position: 'fixed', top: pos.top, left: pos.left, zIndex: 1500 } : { visibility: 'hidden', position: 'fixed', top: 0, left: 0, zIndex: 1500 }}
      onClick={(ev) => ev.stopPropagation()}
    >
      <div className="cal-popover-header">
        <span className="cal-popover-title">{label}</span>
        <button className="btn btn-ghost btn-sm cal-popover-close" onClick={onClose}>✕</button>
      </div>
      <div className="cal-popover-body">
        {events.map((e, i) => {
          const color = typeColor[e.event_type ?? 'todo'] ?? 'var(--ink-3)'
          const typeLabel = TYPE_LABEL[e.event_type ?? 'todo'] ?? e.event_type ?? ''
          const isAuto = !!e._auto
          return (
            <div
              key={i}
              className="cal-popover-item"
              style={{ opacity: e.is_done ? 0.5 : 1, cursor: isAuto ? 'default' : 'pointer' }}
              onClick={() => !isAuto && onDetailEvent(e)}
            >
              <div className="cal-popover-dot" style={{ background: color }} />
              <div className="cal-popover-item-body">
                <div className="cal-popover-item-title" style={{
                  color: 'var(--ink)',
                  textDecoration: e.is_done ? 'line-through' : undefined,
                }}>
                  {e.title}
                </div>
                {e.memo && (
                  <div className="cal-popover-item-memo">{e.memo}</div>
                )}
              </div>
              <span className="cal-popover-type-badge" style={{
                background: color + '22', color,
              }}>{typeLabel}</span>
            </div>
          )
        })}
      </div>
    </div>
  )

  return createPortal(popover, document.body)
}

function EventCalendar() {
  const [ym, setYm]               = useState(() => todayStr.slice(0, 7))
  const [events, setEvents]       = useState<any[]>([])
  const [autoEvs, setAutoEvs]     = useState<any[]>([])
  const [addDate, setAddDate]         = useState<string | null>(null)
  const [detailEvent, setDetailEvent] = useState<any>(null)
  const [loading, setLoading]         = useState(false)
  const [popoverDate, setPopoverDate]     = useState<string | null>(null)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)

  const [addForm, setAddForm] = useState({ title: '', event_type: 'todo', memo: '' })
  const [addSaving, setAddSaving] = useState(false)
  const [showFixedEdit, setShowFixedEdit] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { events: evs, autoEvs: aevs } = await fetchMonthScheduleEvents(ym)
      setEvents(evs); setAutoEvs(aevs)
    } finally { setLoading(false) }
  }, [ym])

  useEffect(() => { load() }, [load])

  const year  = Number(ym.split('-')[0])
  const month = Number(ym.split('-')[1])
  const firstDay = new Date(year, month - 1, 1).getDay()
  const daysInMonth = new Date(year, month, 0).getDate()
  const cells: (number | null)[] = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]
  while (cells.length % 7 !== 0) cells.push(null)

  const eventMap = groupEventsByDate([...events, ...autoEvs])

  const handleAddSave = async () => {
    if (!addDate || !addForm.title.trim()) return
    setAddSaving(true)
    try {
      await api.events.create({ event_date: addDate, title: addForm.title, event_type: addForm.event_type, memo: addForm.memo || undefined })
      setAddDate(null); setAddForm({ title: '', event_type: 'todo', memo: '' }); load()
    } finally { setAddSaving(false) }
  }

  const typeColor: Record<string, string> = {
    todo: 'var(--amber)', deadline: 'var(--red)', memo: 'var(--ink-3)', payday: 'var(--green)',
  }

  return (
    <div className="card mt-4">
      <div className="card-header">
        <span className="card-title">カレンダー・TODO</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setShowFixedEdit(true)}>固定予定を編集</button>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            const d = new Date(year, month - 2, 1); setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
          }}>‹</button>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{ym}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            const d = new Date(year, month, 1); setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
          }}>›</button>
        </div>
      </div>
      <div style={{ padding: '12px 16px', overflow: 'hidden' }}>
        {loading && <div className="loading" style={{ padding: 12 }}>LOADING...</div>}
        {/* Portal: カード外に描画するためグリッド外に配置 */}
        {popoverDate && (() => {
          const dayEvs = eventMap.get(popoverDate) ?? []
          return (
            <DayPopover
              anchorEl={popoverAnchor}
              dateStr={popoverDate}
              events={dayEvs}
              typeColor={typeColor}
              onClose={() => { setPopoverDate(null); setPopoverAnchor(null) }}
              onDetailEvent={(e) => { setDetailEvent(e); setPopoverDate(null); setPopoverAnchor(null) }}
            />
          )
        })()}
        {/* カレンダーグリッド: minmax(0,1fr) で文字量によるカラム幅崩れを防止 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 2, marginBottom: 8, width: '100%',
        }}>
          {['日','月','火','水','木','金','土'].map(d => (
            <div key={d} style={{
              textAlign: 'center', fontSize: 10, color: 'var(--ink-3)',
              fontWeight: 600, padding: '4px 0',
            }}>{d}</div>
          ))}
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} style={{ minWidth: 0 }} />
            const dateStr = `${ym}-${String(day).padStart(2, '0')}`
            const isToday = dateStr === todayStr
            const dayEvs  = eventMap.get(dateStr) ?? []
            const dow     = (firstDay + day - 1) % 7
            return (
              <div key={dateStr}
                onClick={() => setAddDate(addDate === dateStr ? null : dateStr)}
                style={{
                  minHeight: 44, padding: '2px 3px', borderRadius: 4, cursor: 'pointer',
                  minWidth: 0, overflow: 'visible', position: 'relative',
                  border: `1px solid ${isToday ? 'var(--amber)' : addDate === dateStr ? 'var(--blue)' : 'var(--paper-3)'}`,
                  background: isToday ? 'var(--amber-light)' : addDate === dateStr ? 'var(--blue-light)' : 'var(--paper)',
                }}
              >
                <div style={{
                  fontSize: 10, fontWeight: isToday ? 700 : 400,
                  color: dow === 0 ? 'var(--red)' : dow === 6 ? 'var(--blue)' : 'var(--ink)',
                  marginBottom: 1,
                }}>{day}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {dayEvs.slice(0, 3).map((e, j) => (
                    <div key={j}
                      onClick={(ev) => { ev.stopPropagation(); setDetailEvent(e) }}
                      style={{
                        fontSize: 9, lineHeight: 1.3, padding: '1px 2px', borderRadius: 2,
                        background: (typeColor[e.event_type ?? 'todo'] ?? 'var(--ink-3)') + '22',
                        color: typeColor[e.event_type ?? 'todo'] ?? 'var(--ink-3)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        textDecoration: e.is_done ? 'line-through' : undefined,
                      }}
                    >{e.title}</div>
                  ))}
                  {dayEvs.length > 3 && (
                    <div
                      className="cal-more-btn"
                      onClick={(ev) => {
                        ev.stopPropagation()
                        if (popoverDate === dateStr) {
                          setPopoverDate(null); setPopoverAnchor(null)
                        } else {
                          setPopoverDate(dateStr); setPopoverAnchor(ev.currentTarget as HTMLElement)
                        }
                      }}
                    >+{dayEvs.length - 3}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {addDate && (
          <div style={{ marginTop: 10, padding: '12px 14px', background: 'var(--blue-light)',
            border: '1px solid rgba(30,95,168,0.15)', borderRadius: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)', marginBottom: 8 }}>
              {addDate} にイベントを追加
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <input style={{ flex: 1, minWidth: 160, fontSize: 13 }}
                placeholder="タイトル" value={addForm.title}
                onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} />
              <select style={{ fontSize: 12 }} value={addForm.event_type}
                onChange={e => setAddForm(f => ({ ...f, event_type: e.target.value }))}>
                <option value="todo">TODO</option>
                <option value="deadline">締切</option>
                <option value="memo">メモ</option>
                <option value="payday">給料日</option>
              </select>
            </div>
            <input style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
              placeholder="メモ（任意）" value={addForm.memo}
              onChange={e => setAddForm(f => ({ ...f, memo: e.target.value }))} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAddDate(null)}>キャンセル</button>
              <button className="btn btn-primary btn-sm" onClick={handleAddSave} disabled={addSaving || !addForm.title.trim()}>
                {addSaving ? '保存中...' : '追加'}
              </button>
            </div>
          </div>
        )}

        {detailEvent && !detailEvent._auto && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
              {detailEvent.event_date} の詳細
            </div>
            <EventCard event={detailEvent} onUpdated={() => { load(); setDetailEvent(null) }} />
          </div>
        )}
      </div>
      {showFixedEdit && (
        <FixedScheduleModal onClose={() => setShowFixedEdit(false)} onSaved={() => { setShowFixedEdit(false); load() }} />
      )}
    </div>
  )
}

// 毎月の固定予定（給与振込 / シフト締切 / 任意の追加予定）を編集する小モーダル。
// 予定名・日付の変更、追加、削除を「保存」1回でまとめて反映する。
// 今月以降にのみ反映し、過去月の表示は変えない（サーバー側で保証）。
type FixedRow = { key: string; id?: string; label: string; day: string }

function FixedScheduleModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [rows, setRows]       = useState<FixedRow[]>([])
  const [orig, setOrig]       = useState<string>('')
  const [month, setMonth]     = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [delTarget, setDelTarget] = useState<FixedRow | null>(null)
  const seq = useRef(0)
  const newKey = () => `new_${++seq.current}`

  const snapshot = (rs: FixedRow[]) => JSON.stringify(rs.map((r) => [r.id ?? null, r.label.trim(), r.day]))

  useEffect(() => {
    api.fixedSchedule.list()
      .then((r) => {
        const rs = r.items.map((i) => ({ key: i.id, id: i.id, label: i.label, day: String(i.day_of_month ?? '') }))
        setRows(rs); setOrig(snapshot(rs)); setMonth(r.month)
      })
      .catch((e: any) => setError(e.message ?? '取得に失敗しました'))
      .finally(() => setLoading(false))
  }, [])

  const dayInvalid = (v: string) => !/^\d+$/.test(v) || Number(v) < 1 || Number(v) > 31
  const rowInvalid = (r: FixedRow) => r.label.trim() === '' || dayInvalid(r.day)
  const dirty   = !loading && snapshot(rows) !== orig
  const canSave = dirty && rows.every((r) => !rowInvalid(r))

  const patch = (key: string, p: Partial<FixedRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)))
  const remove = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key))

  const save = async () => {
    if (saving) return
    setSaving(true); setError(null)
    try {
      await api.fixedSchedule.save(rows.map((r) => ({
        ...(r.id ? { id: r.id } : {}), label: r.label.trim(), day_of_month: Number(r.day),
      })))
      onSaved()
    } catch (e: any) {
      setError(e.message ?? '保存に失敗しました')
      setSaving(false)
    }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title">毎月の固定予定を編集</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading">LOADING...</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>固定予定はありません。</div>
              )}
              {rows.map((r) => (
                <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="text" value={r.label} maxLength={50} placeholder="予定名"
                    onChange={(e) => patch(r.key, { label: e.target.value })}
                    style={{ flex: 1, minWidth: 0, borderColor: r.label.trim() === '' ? 'var(--red)' : undefined }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>毎月</span>
                  <input
                    type="number" min={1} max={31} step={1} inputMode="numeric"
                    value={r.day}
                    onChange={(e) => patch(r.key, { day: e.target.value })}
                    style={{ width: 60, textAlign: 'right', borderColor: dayInvalid(r.day) ? 'var(--red)' : undefined }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>日</span>
                  <button
                    className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                    onClick={() => (r.id ? setDelTarget(r) : remove(r.key))}
                  >削除</button>
                </div>
              ))}
              <div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setRows((rs) => [...rs, { key: newKey(), label: '', day: '' }])}
                >＋ 固定予定を追加</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                {month} 以降の月に反映されます（過去の月は変わりません）。その月に存在しない日（例: 31日の2月）は表示されません。
              </div>
              {error && <div style={{ fontSize: 12, color: 'var(--red)' }}>{error}</div>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>キャンセル</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave || saving}>{saving ? '保存中...' : '保存'}</button>
        </div>
      </div>
      {delTarget && (
        <ConfirmDialog
          title="固定予定の削除"
          message={`『${delTarget.label.trim() || delTarget.label}』を固定予定から削除しますか？\n今後の月には表示されなくなります。`}
          confirmLabel="削除" confirmDanger
          onConfirm={() => { remove(delTarget.key); setDelTarget(null) }}
          onClose={() => setDelTarget(null)}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────
// HandoverPanel（変更なし）
// ────────────────────────────────────────
const d3ago = new Date(Date.now() + 9 * 60 * 60 * 1000 - 3 * 24 * 60 * 60 * 1000)
const d3agoStr = d3ago.toISOString().slice(0, 10)

function HandoverPanel({ onNavigate }: { onNavigate: () => void }) {
  const [startDate, setStartDate] = useState(d3agoStr)
  const [endDate,   setEndDate]   = useState(todayStr)
  const [records,   setRecords]   = useState<any[]>([])
  const [loading,   setLoading]   = useState(false)
  const [err,       setErr]       = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setErr(null)
    try {
      const data = await api.reports.list({
        start_date: startDate, end_date: endDate, handover_only: '1',
      })
      setRecords(data)
    } catch (e: any) {
      setErr(e.message ?? '取得に失敗しました')
    } finally { setLoading(false) }
  }, [startDate, endDate])

  useEffect(() => { load() }, [load])

  return (
    <div className="card">
      <div className="card-header">
        {/* ★ タイトル部分のみクリックで遷移（ヘッダー全体はクリックしない） */}
        <span
          className="card-title"
          style={{ cursor: 'pointer' }}
          onClick={onNavigate}
        >
          引き継ぎ事項 ↗
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}>
          <input type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ fontSize: 12, width: 130 }} />
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>〜</span>
          <input type="date" value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ fontSize: 12, width: 130 }} />
          <button className="btn btn-outline btn-sm" onClick={load} style={{ fontSize: 11 }}>
            検索
          </button>
        </div>
      </div>
      <div>
        {loading && <div className="loading" style={{ padding: 20 }}>LOADING...</div>}
        {err    && <div style={{ color: 'var(--red)', padding: 16, fontSize: 13 }}>{err}</div>}
        {!loading && !err && records.length === 0 && (
          <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--ink-3)' }}>
            引き継ぎ事項なし（期間内に handover_notes のある日報が見つかりません）
          </div>
        )}
        {records.map((r) => (
          <div key={r.id} style={{
            padding: '10px 16px', borderBottom: '1px solid var(--paper-2)',
            display: 'flex', gap: 10,
          }}>
            <div style={{ width: 4, borderRadius: 2, alignSelf: 'stretch', flexShrink: 0, background: 'var(--amber)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{r.assistant_name}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{r.report_date}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {r.handover_notes}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ────────────────────────────────────────
// ドラッグ&ドロップ レイアウト管理
// ────────────────────────────────────────
type ColumnId = 'left' | 'right'
type PanelId  = 'handover' | 'production' | 'bank' | 'kpi' | 'cashflow' |
                'labor' | 'deadline' | 'reports' |
                'attendance' | 'expenses' | 'revenue' | 'calendar'

type DashLayout = { left: PanelId[]; right: PanelId[] }

const LAYOUT_KEY = 'mangaos-dash-layout-v2'

const DEFAULT_LAYOUT: DashLayout = {
  left:  ['handover', 'production', 'bank', 'kpi', 'cashflow'],
  right: ['attendance', 'expenses', 'revenue', 'labor', 'deadline', 'reports', 'calendar'],
}

const PANEL_NAMES: Record<PanelId, string> = {
  handover:    '引き継ぎ事項',
  production:  '今日の制作予定',
  bank:        '口座残高',
  kpi:         'KPI カード',
  cashflow:    '収支構造 / 支出内訳',
  labor:       '人件費推移（6ヶ月）',
  deadline:    '締切管理',
  reports:     '最近の日報',
  attendance:  '今日の出勤',
  expenses:    '未払い支出',
  revenue:     '入金予定',
  calendar:    'カレンダー・TODO',
}

function loadLayout(): DashLayout {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as DashLayout
      // すべての既知 ID が含まれていることを検証
      const allIds = new Set<PanelId>([...parsed.left, ...parsed.right])
      const known  = Object.keys(PANEL_NAMES) as PanelId[]
      if (known.every(id => allIds.has(id))) return parsed
    }
  } catch {}
  return DEFAULT_LAYOUT
}

function saveLayout(layout: DashLayout) {
  try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)) } catch {}
}

// ドラッグ可能なパネルラッパー
function DraggablePanel({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      className="dash-panel-wrapper"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity:   isDragging ? 0.3 : 1,
        zIndex:    isDragging ? 1   : undefined,
      }}
    >
      <button
        className="dash-panel-handle"
        {...attributes}
        {...listeners}
        title="ドラッグして並び替え"
        aria-label="パネルを並び替え"
      >
        ⠿
      </button>
      {children}
    </div>
  )
}

// ────────────────────────────────────────
// メイン Dashboard
// ────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate()
  const [data, setData]           = useState<DashData | null>(null)
  const [err, setErr]             = useState<string | null>(null)
  const [undoState, setUndoState] = useState<{ id: string; name: string } | null>(null)
  const [adjustTarget, setAdjustTarget] = useState<string | null>(null)

  // ── レイアウト状態 ──
  const [layout,   setLayout]   = useState<DashLayout>(loadLayout)
  const [activeId, setActiveId] = useState<PanelId | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor,  { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const findColumn = (id: string): ColumnId | null => {
    if (layout.left.includes(id  as PanelId)) return 'left'
    if (layout.right.includes(id as PanelId)) return 'right'
    return null
  }

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as PanelId)
  }

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) return
    const aId = active.id as string
    const oId = over.id   as string
    const activeCol = findColumn(aId)
    const overCol   = findColumn(oId) ?? (['left','right'].includes(oId) ? oId as ColumnId : null)
    if (!activeCol || !overCol || activeCol === overCol) return

    setLayout(prev => {
      const next = { left: [...prev.left], right: [...prev.right] }
      next[activeCol] = next[activeCol].filter(id => id !== aId) as PanelId[]
      const overIdx = next[overCol].indexOf(oId as PanelId)
      if (overIdx >= 0) next[overCol].splice(overIdx, 0, aId as PanelId)
      else              next[overCol].push(aId as PanelId)
      return next
    })
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null)
    if (!over) return
    const aId = active.id as string
    const oId = over.id   as string
    const activeCol = findColumn(aId)
    const overCol   = findColumn(oId)

    setLayout(prev => {
      // 同列内の並び替え
      if (activeCol && overCol && activeCol === overCol) {
        const col      = prev[activeCol]
        const oldIndex = col.indexOf(aId as PanelId)
        const newIndex = col.indexOf(oId as PanelId)
        if (oldIndex === newIndex) { saveLayout(prev); return prev }
        const next = { ...prev, [activeCol]: arrayMove(col, oldIndex, newIndex) }
        saveLayout(next); return next
      }
      // 列間移動は handleDragOver で完了済み → 保存のみ
      saveLayout(prev); return prev
    })
  }

  const load = useCallback(() => {
    api.dashboard.get()
      .then(setData)
      .catch((e) => setErr(e.message))
  }, [])

  useEffect(() => { load() }, [load])

  if (err)   return <div className="error-box">{err}</div>
  if (!data) return <div className="loading">LOADING...</div>

  const fmt = (n: number) => n.toLocaleString('ja-JP')
  // マイナス金額（返金・調整等）は「-¥5,000」のように符号を¥の前に出す
  const fmtYen = (n: number) => n < 0 ? `-¥${fmt(Math.abs(n))}` : `¥${fmt(n)}`

  // 緊急アラート計算
  const activeDeadlines    = data.upcoming_deadlines.filter((e) => !isDoneEpisode(e))
  const criticalDeadlines  = activeDeadlines.filter((e) => (e.days_until_submit ?? 99) <= 2)
  const overdueExpenses    = data.unpaid_expenses.filter((e) => (e.days_until_due ?? 0) < 0)
  const shiftNeedsReview   = data.shift_needs_review_count ?? 0
  const hasAlert           = criticalDeadlines.length > 0 || overdueExpenses.length > 0 || shiftNeedsReview > 0

  // 残高不足アラート
  const withdrawalAlerts   = data.withdrawal_alerts ?? []
  const bankAccounts       = data.bank_accounts ?? []

  // 調整対象口座
  const adjustAccount = bankAccounts.find((a) => a.account_key === adjustTarget)

  // 人件費（通常アシスタント + ヘルプスタッフの合計。目標比較・キャッシュフロー・グラフもこの合計値を使用）
  const mealTotal        = data.monthly_labor?.meal_total ?? 0
  const normalLaborTotal = (data.monthly_labor?.total ?? 0) + mealTotal
  const helpLaborTotal   = data.monthly_labor?.help_total ?? 0
  const laborTotal       = normalLaborTotal + helpLaborTotal
  const manDays           = data.monthly_labor?.man_days ?? 0
  const laborPct          = Math.round((laborTotal / LABOR_TARGET) * 100)

  const received    = data.monthly_revenue?.received ?? 0
  const taxReserved = data.monthly_revenue?.tax_reserved ?? 0
  const pending     = data.monthly_revenue?.pending_income ?? 0
  const spendable   = data.spendable

  // ── パネル → JSX のマップ ──
  const panelContent: Partial<Record<PanelId, React.ReactNode>> = {
    handover: (
      <HandoverPanel onNavigate={() => navigate('/reports')} />
    ),
    production: (
      <TodayProductionPlanPanel showUpcoming />
    ),
    bank: bankAccounts.length > 0 ? (
      <div className="card" style={{ padding: 12 }}>
        <div className="card-header" style={{ marginBottom: 8 }}>
          <span className="card-title">口座残高</span>
        </div>
        <BankAccountCards accounts={bankAccounts} accountBreakdown={data.account_breakdown} onAdjust={(key) => setAdjustTarget(key)} />
      </div>
    ) : null,
    kpi: (
      <div className="grid-4">
        <div className="metric" style={{
          background: spendable >= 0 ? 'var(--green-light)' : 'var(--red-light)',
          border: `1px solid ${spendable >= 0 ? 'var(--green-border)' : 'var(--red-border)'}`,
        }}>
          <div className="metric-label">今使えるお金</div>
          <div className="metric-value" style={{ color: spendable >= 0 ? 'var(--green)' : 'var(--red)', fontSize: 20 }}>
            ¥{fmt(spendable)}
          </div>
          <div className="metric-sub">入金済 − 税積立 − 未払固定費</div>
        </div>
        <div className="metric">
          <div className="metric-label">今月入金済</div>
          <div className="metric-value">¥{fmt(received)}</div>
          <div className="metric-sub">入金待ち ¥{fmt(pending)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">税積立（先取済）</div>
          <div className="metric-value red">¥{fmt(taxReserved)}</div>
          <div className="metric-sub">{received > 0 ? `${Math.round(taxReserved / received * 100)}%` : '—'}</div>
        </div>
        <div className="metric" style={{ cursor: 'pointer' }} onClick={() => navigate('/reports')}>
          <div className="metric-label">今月人件費（食費込）↗</div>
          <div className={`metric-value ${laborPct > 100 ? 'red' : laborPct > 88 ? 'amber' : ''}`}>
            ¥{fmt(laborTotal)}
          </div>
          <div className="metric-sub">
            目標の {laborPct}% / {manDays}人足
            {helpLaborTotal > 0 && (
              <> （うちヘルプ ¥{fmt(helpLaborTotal)}）</>
            )}
          </div>
        </div>
      </div>
    ),
    cashflow: (
      <div className="grid-2" style={{ gap: 12 }}>
        <div className="card" style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="section-title">収支構造（今月入金 = 100%）</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>¥{fmt(received)}</span>
          </div>
          <CashflowBar
            received={received} taxReserved={taxReserved} labor={laborTotal}
            fixedExpense={data.unpaid_expenses.reduce((s: number, e: any) => e.is_fixed ? s + e.amount : s, 0)}
            spendable={spendable}
          />
        </div>
        <div className="card" style={{ padding: '12px 14px' }}>
          <div style={{ marginBottom: 8 }}><span className="section-title">支出内訳</span></div>
          <DonutChart segments={[
            { label: '人件費', value: laborTotal, color: 'var(--chart-labor)' },
            { label: '固定費', value: data.unpaid_expenses.filter((e: any) => e.is_fixed).reduce((s: number, e: any) => s + e.amount, 0), color: 'var(--chart-fixed)' },
            { label: '変動費', value: data.unpaid_expenses.filter((e: any) => !e.is_fixed).reduce((s: number, e: any) => s + e.amount, 0), color: 'var(--chart-variable)' },
            { label: '税積立', value: taxReserved, color: 'var(--chart-tax)' },
          ].filter((s) => s.value > 0)} />
        </div>
      </div>
    ),
    labor: data.labor_chart.length > 0 ? (
      <div className="card" style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="section-title">人件費推移（6ヶ月）</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
              今月 ¥{fmt(laborTotal)} / 目標 ¥{fmt(LABOR_TARGET)}
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
              color: laborPct > 100 ? 'var(--red)' : laborPct > 88 ? 'var(--amber)' : 'var(--green)',
            }}>{laborPct}%</span>
          </div>
        </div>
        <div style={{ height: 5, background: 'var(--paper-3)', borderRadius: 3, marginBottom: 10 }}>
          <div style={{
            height: '100%', width: `${Math.min(laborPct, 100)}%`,
            background: laborPct > 100 ? 'var(--red)' : laborPct > 88 ? 'var(--amber)' : 'var(--green)',
            borderRadius: 3, transition: 'width 0.4s',
          }} />
        </div>
        <LaborChart data={data.labor_chart} />
      </div>
    ) : null,
    deadline: (
      <div className="card">
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => navigate('/works')}>
          <span className="card-title">締切管理 ↗</span>
          {criticalDeadlines.length > 0 && <span className="badge badge-red">{criticalDeadlines.length}件 緊急</span>}
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {activeDeadlines.length === 0 && <div className="loading" style={{ padding: 20 }}>21日以内の締切なし</div>}
          {activeDeadlines.map((ep) => {
            const days   = ep.days_until_submit
            const urgent = days !== null && days <= 2
            return (
              <div key={ep.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                borderBottom: '1px solid var(--paper-2)',
                background: urgent ? 'rgba(192,57,43,0.04)' : undefined,
              }}>
                <div style={{
                  width: 4, alignSelf: 'stretch', borderRadius: 2, flexShrink: 0,
                  background: days !== null && days < 0 ? 'var(--red)'
                    : days !== null && days <= 2 ? 'var(--red)'
                    : days !== null && days <= 5 ? 'var(--amber)' : 'var(--paper-3)',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {ep.work_title}
                    <span style={{ fontFamily: 'var(--font-mono)', marginLeft: 6, color: 'var(--ink-3)' }}>
                      {ep.work_type && ep.work_type !== 'story' ? (ep.work_label ?? ep.work_type) : `#${ep.episode_number}`}
                    </span>
                  </div>
                  <div style={{ marginTop: 3 }}><StagePips summary={ep.stage_summary} /></div>
                </div>
                <DaysBadge days={days} />
              </div>
            )
          })}
        </div>
      </div>
    ),
    reports: data.recent_reports.length > 0 ? (
      <div className="card">
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => navigate('/reports')}>
          <span className="card-title">最近の日報 ↗</span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>問題あり優先表示</span>
        </div>
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {data.recent_reports.map((r) => {
            const hasIssue = r.issues && r.issues.trim()
            return (
              <div key={r.id} style={{
                display: 'flex', gap: 10, padding: '8px 14px',
                borderBottom: '1px solid var(--paper-2)',
                background: hasIssue ? 'rgba(192,57,43,0.03)' : undefined,
              }}>
                <div style={{ width: 4, borderRadius: 2, alignSelf: 'stretch', flexShrink: 0,
                  background: hasIssue ? 'var(--red)' : 'var(--paper-3)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{r.assistant_name}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{r.report_date}</span>
                    {r.stage && <span className="badge badge-gray" style={{ fontSize: 10 }}>{r.stage}</span>}
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.pages_done}P</span>
                  </div>
                  {r.description && (
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.description}
                    </div>
                  )}
                  {hasIssue && (
                    <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 2 }}>⚠ {r.issues}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    ) : null,
    attendance: (
      <div className="card">
        <div className="card-header">
          <span className="card-title" style={{ cursor: 'pointer' }}
            onClick={() => navigate('/personnel?tab=salary')} title="給与サマリー・勤怠明細を確認">
            今日の出勤 ↗
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {data.today_attendance.filter((a) => a.is_recorded).length} / {data.today_attendance.length}名 記録済
          </span>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <AttendancePanel
            list={data.today_attendance}
            today={data.today}
            onRecorded={(id, name) => { load(); if (id) setUndoState({ id, name }) }}
          />
        </div>
      </div>
    ),
    expenses: (
      <div className="card">
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => navigate('/expenses')}>
          <span className="card-title">未払い支出 ↗</span>
          {overdueExpenses.length > 0 && <span className="badge badge-red">{overdueExpenses.length}件 超過</span>}
        </div>
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {data.unpaid_expenses.length === 0 && <div className="loading" style={{ padding: 20 }}>未払いなし ✓</div>}
          {data.unpaid_expenses.map((e) => {
            const over = (e.days_until_due ?? 0) < 0
            return (
              <div key={e.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                borderBottom: '1px solid var(--paper-2)',
                background: over ? 'rgba(192,57,43,0.04)' : undefined,
              }}>
                <div style={{
                  width: 4, alignSelf: 'stretch', borderRadius: 2, flexShrink: 0,
                  background: over ? 'var(--red)' : (e.days_until_due ?? 99) <= 5 ? 'var(--amber)' : 'var(--paper-3)',
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 12 }}>{e.description}</div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>{e.payment_method}</div>
                </div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                  {fmtYen(e.amount)}
                </span>
                <DaysBadge days={e.days_until_due} />
              </div>
            )
          })}
        </div>
      </div>
    ),
    revenue: (
      <div className="card">
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => navigate('/finance')}>
          <span className="card-title">入金予定 ↗</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            ¥{fmt(data.pending_revenues.reduce((s, r) => s + r.amount, 0))}
          </span>
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {data.pending_revenues.length === 0 && <div className="loading" style={{ padding: 20 }}>入金予定なし</div>}
          {data.pending_revenues.map((r) => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
              borderBottom: '1px solid var(--paper-2)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{r.work_title ?? r.revenue_type}</div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{r.expected_at}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 12 }}>¥{fmt(r.amount)}</div>
                {r.tax_reserve_amount && (
                  <div style={{ fontSize: 10, color: 'var(--red)' }}>税積立 ¥{fmt(r.tax_reserve_amount)}</div>
                )}
              </div>
              <DaysBadge days={r.days_until} />
            </div>
          ))}
        </div>
      </div>
    ),
    calendar: <EventCalendar />,
  }

  // null パネルを除いた有効 ID リスト
  const activeLeft  = layout.left.filter(id  => panelContent[id] != null)
  const activeRight = layout.right.filter(id => panelContent[id] != null)

  return (
    <div>
      {/* ═══ アラートバナー（全幅）══════════════════════════════ */}
      {hasAlert && (
        <div style={{
          background: 'var(--red-light)', border: '1px solid rgba(192,57,43,0.25)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 10,
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--red)', marginBottom: 6 }}>⚠ 要確認</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {criticalDeadlines.map((e) => (
              <div key={e.id} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: 'var(--red)', fontWeight: 600 }}>締切</span>
                <span>
                  {e.work_title}{' '}
                  <strong>
                    {e.work_type && e.work_type !== 'story' ? (e.work_label ?? e.work_type) : `#${e.episode_number}`}
                  </strong>
                </span>
                <DaysBadge days={e.days_until_submit} />
              </div>
            ))}
            {overdueExpenses.map((e) => (
              <div key={e.id} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: 'var(--red)', fontWeight: 600 }}>支払超過</span>
                <span>{e.description}</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtYen(e.amount)}</span>
                <DaysBadge days={e.days_until_due} />
              </div>
            ))}
            {shiftNeedsReview > 0 && (
              <div style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: 'var(--amber)', fontWeight: 600 }}>シフト更新</span>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--amber)' }}
                  onClick={() => navigate('/personnel?tab=shifts')}
                >
                  26日以降に更新されたシフト希望が {shiftNeedsReview}件 あります
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {withdrawalAlerts.length > 0 && <WithdrawalAlertBanner alerts={withdrawalAlerts} />}

      {/* ═══ ドラッグ可能 2カラムグリッド ════════════════════ */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="dash-2col">

          {/* ── 左カラム ── */}
          <SortableContext items={activeLeft} strategy={verticalListSortingStrategy} id="left">
            <div className="dash-col-main">
              {activeLeft.map(id => (
                <DraggablePanel key={id} id={id}>
                  {panelContent[id]}
                </DraggablePanel>
              ))}
            </div>
          </SortableContext>

          {/* ── 右カラム ── */}
          <SortableContext items={activeRight} strategy={verticalListSortingStrategy} id="right">
            <div className="dash-col-side">
              {activeRight.map(id => (
                <DraggablePanel key={id} id={id}>
                  {panelContent[id]}
                </DraggablePanel>
              ))}
            </div>
          </SortableContext>
        </div>

        {/* ドラッグ中のゴーストプレビュー */}
        <DragOverlay>
          {activeId && (
            <div className="card" style={{ padding: '12px 16px', opacity: 0.88, cursor: 'grabbing' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-2)' }}>
                ⠿ {PANEL_NAMES[activeId]}
              </span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {undoState && (
        <UndoToast
          msg={`${undoState.name} の出勤を記録しました — `}
          onUndo={async () => { await api.attendance.delete(undoState.id); await load() }}
          onClose={() => setUndoState(null)}
          extraAction={{ label: '勤怠明細を確認 →', onClick: () => navigate('/personnel?tab=salary') }}
        />
      )}

      {adjustTarget && adjustAccount && (
        <AdjustModal
          accountKey={adjustTarget}
          accountName={adjustAccount.name}
          currentBalance={adjustAccount.current_balance}
          onClose={() => setAdjustTarget(null)}
          onAdjusted={() => { setAdjustTarget(null); load() }}
        />
      )}
    </div>
  )
}